import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWhiteboardStore } from '../store/useWhiteboardStore';

interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  action: () => void;
  description: string;
  global?: boolean; // If true, works even when input is focused
}

// Global keyboard shortcuts
const globalShortcuts: KeyboardShortcut[] = [
  {
    key: 'Escape',
    action: () => {
      // Close any open modals or dialogs
      const event = new CustomEvent('close-modal');
      window.dispatchEvent(event);
    },
    description: 'Close modal/dialog',
    global: true
  },
  {
    key: '/',
    ctrl: true,
    action: () => {
      // Focus search input
      const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
      searchInput?.focus();
    },
    description: 'Focus search'
  },
  {
    key: 'k',
    ctrl: true,
    action: () => {
      // Open command palette
      const event = new CustomEvent('open-command-palette');
      window.dispatchEvent(event);
    },
    description: 'Open command palette'
  }
];

// Whiteboard-specific shortcuts
const whiteboardShortcuts: KeyboardShortcut[] = [
  {
    key: 'p',
    action: () => useWhiteboardStore.getState().setCurrentTool('pencil'),
    description: 'Select pencil tool'
  },
  {
    key: 'e',
    action: () => useWhiteboardStore.getState().setCurrentTool('eraser'),
    description: 'Select eraser tool'
  },
  {
    key: 'h',
    action: () => useWhiteboardStore.getState().setCurrentTool('highlighter'),
    description: 'Select highlighter tool'
  },
  {
    key: 's',
    action: () => useWhiteboardStore.getState().setCurrentTool('select'),
    description: 'Select selection tool'
  },
  {
    key: 'l',
    action: () => useWhiteboardStore.getState().setCurrentTool('laser'),
    description: 'Select laser pointer'
  },
  {
    key: 'z',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('undo');
      window.dispatchEvent(event);
    },
    description: 'Undo'
  },
  {
    key: 'z',
    ctrl: true,
    shift: true,
    action: () => {
      const event = new CustomEvent('redo');
      window.dispatchEvent(event);
    },
    description: 'Redo'
  },
  {
    key: 'Delete',
    action: () => {
      const event = new CustomEvent('delete-selected');
      window.dispatchEvent(event);
    },
    description: 'Delete selected'
  },
  {
    key: 'a',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('select-all');
      window.dispatchEvent(event);
    },
    description: 'Select all'
  },
  {
    key: 'c',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('copy');
      window.dispatchEvent(event);
    },
    description: 'Copy'
  },
  {
    key: 'v',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('paste');
      window.dispatchEvent(event);
    },
    description: 'Paste'
  },
  {
    key: 'x',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('cut');
      window.dispatchEvent(event);
    },
    description: 'Cut'
  },
  {
    key: '+',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('zoom-in');
      window.dispatchEvent(event);
    },
    description: 'Zoom in'
  },
  {
    key: '-',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('zoom-out');
      window.dispatchEvent(event);
    },
    description: 'Zoom out'
  },
  {
    key: '0',
    ctrl: true,
    action: () => {
      const event = new CustomEvent('zoom-reset');
      window.dispatchEvent(event);
    },
    description: 'Reset zoom'
  },
  {
    key: 'ArrowLeft',
    action: () => {
      const event = new CustomEvent('prev-question');
      window.dispatchEvent(event);
    },
    description: 'Previous question'
  },
  {
    key: 'ArrowRight',
    action: () => {
      const event = new CustomEvent('next-question');
      window.dispatchEvent(event);
    },
    description: 'Next question'
  }
];

export function useKeyboardNavigation(shortcuts: KeyboardShortcut[] = []) {
  const navigate = useNavigate();
  const activeElement = useRef<Element | null>(null);

  // Check if the current element is an input
  const isInputFocused = useCallback(() => {
    const focused = document.activeElement;
    return focused && (
      focused.tagName === 'INPUT' ||
      focused.tagName === 'TEXTAREA' ||
      focused.getAttribute('contenteditable') === 'true'
    );
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Skip if input is focused (unless shortcut is global)
    if (isInputFocused() && !shortcuts.some(s => 
      s.key && s.key === event.key && s.global
    )) {
      return;
    }

    // Find matching shortcut
    const shortcut = shortcuts.find(s => {
      if (!s || !s.key || typeof s.key !== 'string') return false;
      const eventKey = event.key;
      if (!eventKey || typeof eventKey !== 'string') return false;
      
      const keyMatch = s.key.toLowerCase() === eventKey.toLowerCase();
      const ctrlMatch = s.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
      const shiftMatch = s.shift ? event.shiftKey : !event.shiftKey;
      const altMatch = s.alt ? event.altKey : !event.altKey;
      
      return keyMatch && ctrlMatch && shiftMatch && altMatch;
    });

    if (shortcut) {
      event.preventDefault();
      shortcut.action();
    }
  }, [shortcuts, isInputFocused]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus management
  const focusNext = useCallback(() => {
    const focusableElements = document.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const currentIndex = Array.from(focusableElements).indexOf(
      document.activeElement as Element
    );
    
    if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
      (focusableElements[currentIndex + 1] as HTMLElement).focus();
    }
  }, []);

  const focusPrevious = useCallback(() => {
    const focusableElements = document.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const currentIndex = Array.from(focusableElements).indexOf(
      document.activeElement as Element
    );
    
    if (currentIndex > 0) {
      (focusableElements[currentIndex - 1] as HTMLElement).focus();
    }
  }, []);

  // Focus trap for modals
  const trapFocus = useCallback((containerRef: React.RefObject<HTMLElement>) => {
    if (!containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    containerRef.current.addEventListener('keydown', handleTabKey);
    firstElement.focus();

    return () => {
      containerRef.current?.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  return {
    focusNext,
    focusPrevious,
    trapFocus,
    isInputFocused
  };
}

// Hook for global keyboard shortcuts
export function useGlobalKeyboardShortcuts() {
  const allShortcuts = [...globalShortcuts];
  
  // Add whiteboard shortcuts if on whiteboard page
  if (window.location.pathname.includes('whiteboard')) {
    allShortcuts.push(...whiteboardShortcuts);
  }
  
  useKeyboardNavigation(allShortcuts);
  
  return allShortcuts;
}

// Hook for custom keyboard shortcuts
export function useCustomKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  useKeyboardNavigation(shortcuts);
}

// Command palette hook
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const shortcuts = useGlobalKeyboardShortcuts();

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    window.addEventListener('open-command-palette', handleOpen);
    window.addEventListener('close-modal', handleClose);

    return () => {
      window.removeEventListener('open-command-palette', handleOpen);
      window.removeEventListener('close-modal', handleClose);
    };
  }, []);

  return {
    isOpen,
    setIsOpen,
    shortcuts
  };
}

// Focus visible hook
export function useFocusVisible() {
  useEffect(() => {
    // Add focus-visible class to body when using keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-navigation');
      }
    };

    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-navigation');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
}

// Import React hooks
import { useState } from 'react';